import React from "react";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import { Space } from "../../space/Space";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import LoaderBox from "../../loader/LoaderBox";
import { useAppSelector } from "../../../app/store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { AddAttachmentsProps } from "./types";
import { useAttachmentSelection, usePresignedUrl, useSubmitAttachments } from "./hooks";
import { AttachmentTable } from "./AttachmentTable";
import { handleBackToProperty } from "../utils/utils";

export const AddAttachments: React.FC<AddAttachmentsProps> = ({ onNext, onPrevious, data }) => {
    const navigate = useNavigate();
    const { propertyId } = useParams();
    const [updateAppeal, { isLoading: isUpdating }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);

    const attachments =
        data?.appealDetails?.appealApproachCalculationData.exportAppealsPackage.attachments || [];

    const { selected, onChangeRow, handleSelectAll, handleClearSelection } =
        useAttachmentSelection(attachments);
    const { fetchPresignedUrl } = usePresignedUrl();

    const handleBack = () => {
        handleBackToProperty({
            data,
            propertyId: propertyId!,
            alias,
            updateAppeal,
            navigate,
        });
    };

    const { handleSubmitAttachments, pdfLoading } = useSubmitAttachments({
        data,
        attachments,
        selected,
        alias,
        onNext,
    });

    if (isUpdating || pdfLoading) {
        return <LoaderBox loadingText="Generating PDF..." />;
    }

    return (
        <Box>
            <Space size={"l"} />
            <Row alignmentHorizontal={"justify"}>
                <Box>
                    <Text type={"h500"}>Add Attachments</Text>
                    <Text>Select attachments to be included in the Appeals package</Text>
                </Box>
                <Row alignmentHorizontal={"end"}>
                    <Button type={"secondary"} onClick={handleClearSelection}>
                        Clear Selection
                    </Button>
                    <Button type={"secondary"} onClick={handleSelectAll}>
                        Select All
                    </Button>
                </Row>
            </Row>
            <Space size={"s"} />

            <Box type={"outline"} spacingInset={"500"}>
                <Box>
                    <Text type={"h500"}>Add Attachments</Text>
                </Box>
                <Space size={"s"} />
                {attachments.length === 0 ? (
                    <Alert type="warning" size="medium">
                        No attachments available to show.
                    </Alert>
                ) : (
                    <AttachmentTable
                        attachments={attachments}
                        selected={selected}
                        onChangeRow={onChangeRow}
                        fetchPresignedUrl={fetchPresignedUrl}
                    />
                )}
            </Box>

            <Space size={"s"} />
            <Row alignmentHorizontal={"end"} spacingInset={"200"}>
                <Button type={"link"} size={"large"} onClick={handleBack}>
                    Back to Property
                </Button>
                <Button size={"large"} type={"secondary"} onClick={onPrevious}>
                    Back
                </Button>
                <Button size={"large"} onClick={handleSubmitAttachments}>
                    Next
                </Button>
            </Row>
        </Box>
    );
};
